import { GridItem, GridSpacer } from '@bnnvara/grids';
import { Loading as LoadingIcon } from '@bnnvara/icons/src/components/UserInterfaceIcons/Loading';
import { Text } from '@bnnvara/typography';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';

import ScrollIntoView from '../ScrollIntoView';
import usePaginator from './hooks/usePaginator';

const Paginator = ({
  query,
  variables,
  itemsKey,
  limit,
  ItemComponent,
  transformItemProps,
  loadMoreElement,
  noResultText,
}) => {
  const paginatorResult = usePaginator({
    query,
    variables,
    itemsKey,
    limit,
  });

  const { loading, firstLoad, items, firstNewItem, hasNextPage, loadMore } =
    paginatorResult;

  if (firstLoad) {
    return (
      <GridItem
        columnStartSm="1"
        columnEndSm="span 12"
        columnStartMd="3"
        columnEndMd="11"
        centered
      >
        <LoadingIcon />
      </GridItem>
    );
  }

  if (items.length === 0) {
    return (
      <GridItem columnEndSm="span 12">
        <GridSpacer />
        <Text center>
          <p>{noResultText}</p>
        </Text>
      </GridItem>
    );
  }

  return (
    <Fragment>
      {items.map((item, index) => {
        const { id } = item;
        const isFirstNewItem = firstNewItem && id === firstNewItem.id;
        const itemProps = transformItemProps(item);

        if (isFirstNewItem) {
          return (
            <ScrollIntoView key={id}>
              <ItemComponent {...itemProps} index={index} />
            </ScrollIntoView>
          );
        }

        return <ItemComponent key={id} {...itemProps} index={index} />;
      })}

      {hasNextPage && (
        <GridItem
          columnStartSm="1"
          columnEndSm="span 12"
          columnStartMd="3"
          columnEndMd="11"
          centered
        >
          {loading ? (
            <LoadingIcon />
          ) : (
            React.cloneElement(loadMoreElement, {
              onClick: loadMore,
            })
          )}
        </GridItem>
      )}
    </Fragment>
  );
};

Paginator.propTypes = {
  /**
   * GraphQL Query to execute.
   */
  query: PropTypes.shape({
    kind: PropTypes.oneOf(['Document']).isRequired,
  }).isRequired,

  /**
   * Variables that are required for the GraphQL query.
   * `limit` will be automatically added when loading more items.
   */
  variables: PropTypes.shape({
    paginator: PropTypes.shape({
      limit: PropTypes.number,
    }),
  }).isRequired,

  /**
   * The key of the items, for example `brand.articles` or `brand.videos`.
   */
  itemsKey: PropTypes.string.isRequired,

  /**
   * Function which passes true props.
   */
  transformItemProps: PropTypes.func,

  /**
   * Maximum number of items per page.
   */
  limit: PropTypes.number,

  /**
   * A component that renders an individual item.
   */
  ItemComponent: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.elementType }),
  ]).isRequired,

  /**
   * The element that should load more items.
   */
  loadMoreElement: PropTypes.node.isRequired,

  /**
   * The text that is shown when there are 0 results.
   */
  noResultText: PropTypes.string,
};

Paginator.defaultProps = {
  limit: 8,
  noResultText: 'Helaas, we hebben geen resultaten gevonden...',
  transformItemProps: (item) => item,
};

export default Paginator;
